import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Badge, Button, EditArticleButton, StatusDot } from '@/components/atoms';
import { formatarDataHora } from '@/i18n/formato';
import {
  useAddManifestation,
  useDeleteStory,
  useRemoveManifestation,
  useStory,
  useUpdateStory,
} from '@/hooks/useStories';
import type { ManifestationKind } from '@/types/story';

import styles from './StoryDetail.module.css';

export function StoryDetail() {
  const { t } = useTranslation(['articles', 'common']);
  const params = useParams<{ id: string }>();
  const id = params.id!;
  const navigate = useNavigate();
  const { data: story, isLoading } = useStory(id);
  const updateMutation = useUpdateStory();
  const deleteMutation = useDeleteStory();
  const addManif = useAddManifestation();
  const removeManif = useRemoveManifestation();

  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const [newKind, setNewKind] = useState<ManifestationKind>('wp_post');
  const [newExternalId, setNewExternalId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');

  const kindOptions: { value: ManifestationKind; label: string }[] = [
    { value: 'wp_post', label: t('articles:story_detail.kinds.wp_post') },
    { value: 'tweet', label: t('articles:story_detail.kinds.tweet') },
    { value: 'dalle_image', label: t('articles:story_detail.kinds.dalle_image') },
    { value: 'horoscope_sign', label: t('articles:story_detail.kinds.horoscope_sign') },
    { value: 'other', label: t('articles:story_detail.kinds.other') },
  ];

  const getKindLabel = (kind: ManifestationKind) =>
    t(`articles:story_detail.kind_labels.${kind}`, { defaultValue: t('articles:story_detail.kind_labels.other') });

  useEffect(() => {
    if (story) {
      setEditTitle(story.title);
      setEditDesc(story.description ?? '');
    }
  }, [story]);

  if (isLoading) return <p className={styles.muted}>{t('common:status.loading')}</p>;
  if (!story) return <p>{t('articles:story_detail.not_found')}</p>;

  async function handleSave() {
    try {
      await updateMutation.mutateAsync({
        id,
        payload: { title: editTitle, description: editDesc || undefined },
      });
      setEditing(false);
    } catch {
      // hook handles error state
    }
  }

  async function handleArchive() {
    await updateMutation.mutateAsync({
      id,
      payload: { status: story!.status === 'active' ? 'archived' : 'active' },
    });
  }

  async function handleDelete() {
    if (!confirm(t('articles:story_detail.delete_confirm'))) return;
    try {
      await deleteMutation.mutateAsync(id);
      navigate('/stories');
    } catch {
      // hook handles error state
    }
  }

  async function handleAddManif() {
    if (!newExternalId.trim()) return;
    try {
      await addManif.mutateAsync({
        storyId: id,
        payload: {
          kind: newKind,
          external_id: newExternalId.trim(),
          external_url: newUrl.trim() || undefined,
          label: newLabel.trim() || undefined,
        },
      });
      setNewExternalId('');
      setNewLabel('');
      setNewUrl('');
    } catch {
      // hook handles error
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <Link to="/stories" className={styles.back}>
          {t('articles:story_detail.back_link')}
        </Link>
        {editing ? (
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className={styles.titleInput}
          />
        ) : (
          <h1 className={styles.title}>{story.title}</h1>
        )}
        <Badge tone={story.status === 'active' ? 'ok' : 'neutral'} size="sm">
          {story.status}
        </Badge>
      </header>

      {editing ? (
        <textarea
          value={editDesc}
          onChange={(e) => setEditDesc(e.target.value)}
          rows={3}
          placeholder={t('articles:story_detail.desc_placeholder')}
          className={styles.descInput}
        />
      ) : (
        story.description && <p className={styles.desc}>{story.description}</p>
      )}

      <div className={styles.actions}>
        {editing ? (
          <>
            <Button onClick={handleSave} loading={updateMutation.isPending}>
              {t('common:actions.save')}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              {t('common:actions.cancel')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setEditing(true)}>
              {t('common:actions.edit')}
            </Button>
            <Button
              variant="ghost"
              onClick={handleArchive}
              loading={updateMutation.isPending}
            >
              {story.status === 'active'
                ? t('articles:story_detail.action_archive')
                : t('articles:story_detail.action_reactivate')}
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              {t('common:actions.delete')}
            </Button>
          </>
        )}
      </div>

      <h2 className={styles.sectionTitle}>
        {t('articles:story_detail.section_manifestations', {
          count: story.manifestations.length,
        })}
      </h2>

      <div className={styles.addCard}>
        <p className={styles.addCardTitle}>{t('articles:story_detail.add_card_title')}</p>
        <div className={styles.addCardGrid}>
          <select
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as ManifestationKind)}
            className={styles.select}
          >
            {kindOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder={t('articles:story_detail.input_external_id')}
            value={newExternalId}
            onChange={(e) => setNewExternalId(e.target.value)}
            className={styles.input}
          />
          <input
            type="text"
            placeholder={t('articles:story_detail.input_label')}
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            className={styles.input}
          />
          <input
            type="url"
            placeholder={t('articles:story_detail.input_url')}
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            className={styles.input}
          />
          <Button
            onClick={handleAddManif}
            disabled={!newExternalId.trim()}
            loading={addManif.isPending}
          >
            {t('articles:story_detail.btn_link')}
          </Button>
        </div>
      </div>

      <ul className={styles.manifList}>
        {story.manifestations.length === 0 && (
          <li className={styles.empty}>
            {t('articles:story_detail.empty_manifestations')}
          </li>
        )}
        {story.manifestations.map((m) => (
          <li key={m.id} className={styles.manifRow}>
            <span className={styles.kindLabel}>
              {getKindLabel(m.kind)}
            </span>
            <div className={styles.manifBody}>
              <div className={styles.manifTitle}>
                <Badge tone="info" size="xs">
                  {m.kind}
                </Badge>
                <strong>{m.label ?? `#${m.external_id}`}</strong>
              </div>
              <div className={styles.manifMeta}>
                <span>id: {m.external_id}</span>
                {m.site && <span>site: {m.site}</span>}
                <span>
                  {t('articles:story_detail.linked_at')}{' '}
                  {formatarDataHora(m.linked_at, {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
            {m.kind === 'wp_post' && (
              <EditArticleButton id={m.external_id} size="sm" variant="ghost" />
            )}
            {m.external_url && (
              <a
                href={m.external_url}
                target="_blank"
                rel="noreferrer"
                className={styles.smallLink}
              >
                {t('articles:story_detail.external_link')}
              </a>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                removeManif.mutate({ storyId: id, manifestationId: m.id })
              }
            >
              <StatusDot state="crit" /> {t('common:actions.remove')}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
